/**
 * pages/Material/MaterialUsageForm.tsx - 材料使用记录表单组件
 *
 * 功能说明：
 * - 材料使用记录创建/编辑表单
 * - 集成材料选择、供应商选择、价格配置等功能
 *
 * 依赖关系：
 * - 依赖 antd 组件
 * - 依赖 @tanstack/react-query
 * - 依赖 services
 * - 依赖 components/MaterialSelector, SupplierSelect, PriceInput
 *
 * 关键注释索引：
 * - [表单提交] - 见下方 handleSubmit
 * - [价格自动匹配] - 见下方 handleUnitChange
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 * 重构日期：2026-05-03 - 提取子组件到 components 目录
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Select,
  DatePicker,
  message,
  InputNumber,
  AutoComplete,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { MaterialUsageService } from '../../services/materialUsageService';
import { ProjectService } from '../../services/projectService';
import supplierService from '../../services/supplierService';
import { PresetDataService } from '../../services/presetDataService';
import { materialPriceService } from '../../services/materialPriceService';
import MobileForm from '../../responsive/components/MobileForm';
import {
  MaterialUsage,
  CreateMaterialUsageData,
  UpdateMaterialUsageData,
} from '../../types';
import {
  MaterialSelector,
  SupplierSelect,
  PriceInput,
  getMaterialDefaultUnit,
} from './components';
import { hasMoreThan3Decimals, normalizeMaterialPrecision } from '../../utils/materialPrecision';

const { Option } = Select;

interface MaterialUsageFormProps {
  initialValues?: MaterialUsage | null;
  defaultProjectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * [材料使用表单] 材料使用记录创建/编辑表单组件
 */
const MaterialUsageForm: React.FC<MaterialUsageFormProps> = ({
  initialValues,
  defaultProjectId,
  onSuccess,
  onCancel,
}) => {
  const [selectedMaterialName, setSelectedMaterialName] = useState<string>('');
  const [formInstance, setFormInstance] = useState<any>(null);

  const isEditing = !!initialValues;

  // ==================== 数据查询 ====================

  /** 获取活跃项目列表 */
  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => ProjectService.getActiveProjects(),
  });

  /** 获取材料供应商列表 */
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers', 'material-form'],
    queryFn: () => supplierService.listSuppliers('', 200, 'material'),
  });

  /** 获取预设材料列表 */
  const { data: presetMaterialsData } = useQuery({
    queryKey: ['preset-materials', 'all'],
    queryFn: () => PresetDataService.getMaterials({ limit: 500 }),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  /** 获取材料价格配置（根据选中的材料名称） */
  const { data: materialPriceList } = useQuery({
    queryKey: ['material-price-list', selectedMaterialName],
    queryFn: async () => {
      if (!selectedMaterialName) return [];
      try {
        const response = await materialPriceService.getMaterialPrices({
          materialName: selectedMaterialName,
        });
        return response?.data || [];
      } catch (error) {
        console.error('获取材料价格失败:', error);
        return [];
      }
    },
    enabled: !!selectedMaterialName,
  });

  /** 获取常用单位列表 */
  const { data: commonUnitsData } = useQuery({
    queryKey: ['common-units'],
    queryFn: () => MaterialUsageService.getCommonUnits(),
  });

  const [unitInput, setUnitInput] = useState('');

  // ==================== 计算属性 ====================

  /** 根据单位查找价格配置 */
  const findPriceByUnit = (unit: string) => {
    if (!materialPriceList || materialPriceList.length === 0) return null;
    return materialPriceList.find((p: any) => p.unit === unit);
  };

  /** 当前选中单位对应的价格配置 */
  const currentPriceConfig = useMemo(() => {
    const currentUnit = formInstance?.getFieldValue('unit');
    if (currentUnit && materialPriceList) {
      const match = findPriceByUnit(currentUnit);
      if (match) return match;
    }
    return materialPriceList?.[0] || null;
  }, [materialPriceList, formInstance?.getFieldValue('unit')]);

  /** 单位选项（支持搜索过滤） */
  const unitOptions = useMemo(() => {
    const units = commonUnitsData?.data || [];
    return units
      .filter((u: string) => u.toLowerCase().includes(unitInput.toLowerCase()))
      .map((u: string) => ({ value: u, label: u }));
  }, [commonUnitsData, unitInput]);

  // ==================== 数据提交 ====================

  /** 创建材料使用记录 */
  const createMutation = useMutation({
    mutationFn: (data: CreateMaterialUsageData) =>
      MaterialUsageService.createMaterialUsage(data),
    onSuccess: () => {
      message.success('材料使用记录创建成功');
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '创建失败');
    },
  });

  /** 更新材料使用记录 */
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMaterialUsageData }) =>
      MaterialUsageService.updateMaterialUsage(id, data),
    onSuccess: () => {
      message.success('材料使用记录更新成功');
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '更新失败');
    },
  });

  // ==================== 表单处理 ====================

  /** 获取表单初始值 */
  const getInitialValues = () => {
    if (initialValues) {
      return {
        projectId: initialValues.projectId,
        materialName: initialValues.materialName,
        supplierId: initialValues.supplierId,
        usageDate: dayjs(initialValues.usageDate),
        quantity: initialValues.quantity,
        unit: initialValues.unit,
        unitPrice: initialValues.unitPrice,
        constructionType: initialValues.constructionType || null,
      };
    } else {
      return {
        projectId: defaultProjectId,
        usageDate: dayjs(),
      };
    }
  };

  /**
   * [表单提交] 处理表单提交
   */
  const handleSubmit = async (values: any) => {
    if (hasMoreThan3Decimals(values.quantity) || hasMoreThan3Decimals(values.unitPrice)) {
      message.warning('材料数量和单价最多保留 3 位小数，已自动截断');
    }

    const formData = normalizeMaterialPrecision({
      projectId: values.projectId,
      materialName: values.materialName,
      supplierId: values.supplierId || undefined,
      usageDate: values.usageDate.format('YYYY-MM-DD'),
      quantity: values.quantity,
      unit: values.unit,
      unitPrice: values.unitPrice,
      constructionType: values.constructionType || null,
    });

    if (isEditing && initialValues) {
      updateMutation.mutate({
        id: initialValues._id,
        data: formData,
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  /**
   * [材料选择] 当选择材料时，自动填充默认单位
   */
  const handleMaterialSelect = (value: string) => {
    setSelectedMaterialName(value);
    const defaultUnit = getMaterialDefaultUnit(value, presetMaterialsData);
    if (defaultUnit) {
      formInstance?.setFieldValue('unit', defaultUnit);
    }
  };

  /**
   * [单位变化] 当单位变化时，自动匹配价格
   */
  const handleUnitChange = (value: string) => {
    if (materialPriceList && value) {
      const match = materialPriceList.find((p: any) => p.unit === value);
      if (match) {
        formInstance?.setFieldValue('unitPrice', match.price);
        message.info(`已自动匹配单位 "${value}" 的价格: ¥${match.price}`);
      }
    }
  };

  // ==================== 副作用处理 ====================

  /** 编辑模式下回填项目ID */
  useEffect(() => {
    if (isEditing && formInstance && projectsData?.data && initialValues?.projectId) {
      formInstance.setFieldValue('projectId', initialValues.projectId);
    }
  }, [projectsData, formInstance, isEditing, initialValues?.projectId]);

  /** 当价格数据加载完成时，尝试填充默认价格 */
  useEffect(() => {
    if (materialPriceList && materialPriceList.length > 0 && !isEditing && formInstance) {
      const currentUnit = formInstance.getFieldValue('unit');
      if (!currentUnit) {
        const defaultPrice = materialPriceList[0];
        formInstance.setFieldValue('unit', defaultPrice.unit);
        formInstance.setFieldValue('unitPrice', defaultPrice.price);
      } else {
        const match = materialPriceList.find((p: any) => p.unit === currentUnit);
        if (match && !formInstance.getFieldValue('unitPrice')) {
          formInstance.setFieldValue('unitPrice', match.price);
        }
      }
    }
  }, [materialPriceList, formInstance, isEditing]);

  // ==================== 表单渲染 ====================

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <MobileForm
        initialValues={getInitialValues()}
        onFinish={handleSubmit}
        disabled={isLoading}
        submitText={isEditing ? '更新' : '创建'}
        onCancel={onCancel}
        submitLoading={isLoading}
        onFormInstanceReady={setFormInstance}
        fields={[
          {
            name: 'projectId',
            label: '项目',
            rules: [{ required: true, message: '请选择项目' }],
            children: (
              <Select
                placeholder="请选择项目"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
              >
                {projectsData?.data?.map((project) => (
                  <Option key={project._id} value={project._id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            ),
          },
          {
            name: 'materialName',
            label: '材料名称',
            rules: [
              { required: true, message: '请输入材料名称' },
              { max: 100, message: '材料名称不能超过100个字符' },
            ],
            children: (
              <MaterialSelector
                value={undefined}
                onChange={(val) => setSelectedMaterialName(val)}
                onSelect={handleMaterialSelect}
                presetMaterialsData={presetMaterialsData}
                disabled={isLoading}
              />
            ),
          },
          {
            name: 'usageDate',
            label: '使用日期',
            rules: [{ required: true, message: '请选择使用日期' }],
            children: (
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                placeholder="请选择使用日期"
              />
            ),
          },
          {
            name: 'supplierId',
            label: '供应商',
            children: (
              <SupplierSelect
                suppliersData={suppliersData}
                disabled={isLoading}
              />
            ),
          },
          {
            name: 'quantity',
            label: '使用数量',
            rules: [{ required: true, message: '请输入使用数量' }],
            children: (
              <InputNumber
                style={{ width: '100%' }}
                placeholder="请输入使用数量"
                min={0}
                step={0.001}
                precision={3}
              />
            ),
          },
          {
            name: 'unit',
            label: '计量单位',
            rules: [{ max: 20, message: '计量单位不能超过20个字符' }],
            children: (
              <AutoComplete
                placeholder="请输入计量单位"
                options={unitOptions}
                style={{ width: '100%' }}
                onChange={(value) => {
                  setUnitInput(value);
                  handleUnitChange(value);
                }}
                onSearch={(value) => setUnitInput(value)}
                filterOption={(inputValue, option) =>
                  String(option?.value || '').toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            ),
          },
          {
            name: 'constructionType',
            label: '施工类型',
            children: (
              <Select allowClear placeholder="请选择施工类型">
                <Option value="园林">园林</Option>
                <Option value="土建">土建</Option>
                <Option value="外墙">外墙</Option>
                <Option value="其它">其它</Option>
              </Select>
            ),
          },
          {
            name: 'unitPrice',
            label: (
              <span>
                单价（元）
                {currentPriceConfig && (
                  <span style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                    已关联价格配置 (¥{currentPriceConfig.price}/{currentPriceConfig.unit})
                  </span>
                )}
              </span>
            ),
            rules: [
              {
                type: 'number',
                min: 0,
                message: '单价不能为负数',
                transform: (value: any) => {
                  if (value === '' || value === null || value === undefined) {
                    return undefined;
                  }
                  return Number(value);
                },
              },
            ],
            extra: '可选，填写后将自动计算总成本',
            children: (
              <PriceInput
                priceConfig={currentPriceConfig}
                disabled={isLoading}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default MaterialUsageForm;
